import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { auth, storage, db } from "./firebase";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import {
  addDoc,
  collection,
  serverTimestamp,
  onSnapshot,
  doc,
  updateDoc,
  getDoc,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useDropzone } from "react-dropzone";
import { v4 as uuidv4 } from "uuid";
import imageCompression from "browser-image-compression";
import "./upload-foto.css";

const UploadImg = () => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const { id: editId } = useParams();
  const isEditMode = !!editId;

  const [title, setTitle] = useState("");
  const [subDescription, setSubDescription] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]);

  const [originalUrls, setOriginalUrls] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const [clickCountIndex, setClickCountIndex] = useState(null);
  const [clickCount, setClickCount] = useState(0);

  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState("");

  const navigate = useNavigate();
  const maxFileSize = 5 * 1024 * 1024;

  // ─── Auth + Admin check ───────────────────────────────────────────────────
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(
      auth,
      async (loggedInUser) => {
        if (!loggedInUser) {
          navigate("/login");
          setLoading(false);
          return;
        }

        setUser(loggedInUser);

        // Check isAdmin in Firestore
        try {
          const userDoc = await getDoc(doc(db, "users", loggedInUser.uid));
          if (userDoc.exists() && userDoc.data().isAdmin === true) {
            setIsAdmin(true);
          } else {
            setIsAdmin(false);
            setErrorMsg(
              "Je hebt geen beheerdersrechten om foto's te uploaden. " +
              "Zorg dat jouw gebruikersdocument in Firestore het veld isAdmin: true bevat."
            );
          }
        } catch (e) {
          setErrorMsg("Kon beheerdersrechten niet controleren: " + e.message);
        }

        setLoading(false);
      },
      (authError) => {
        setError(authError);
        setLoading(false);
      }
    );
    return () => unsubscribe();
  }, [navigate]);

  // ─── Load existing data in edit mode ─────────────────────────────────────
  useEffect(() => {
    if (isEditMode) {
      const docRef = doc(db, "fotos", editId);
      const unsub = onSnapshot(
        docRef,
        (snap) => {
          if (!snap.exists()) {
            setErrorMsg("Document bestaat niet.");
            return;
          }
          const data = snap.data();
          setTitle(data.title || "");
          setSubDescription(data.subDescription || "");
          setDescription(data.description || "");

          const existing = data.images.map((url) => ({
            isExisting: true,
            url,
          }));
          setImages(existing);
          setOriginalUrls(data.images);
          setSelectedIndex(0);
        },
        () => setErrorMsg("Fout bij ophalen van gegevens voor bewerking.")
      );
      return () => unsub();
    }
  }, [isEditMode, editId]);

  // ─── Dropzone ─────────────────────────────────────────────────────────────
  const onDrop = async (acceptedFiles) => {
    const validFiles = acceptedFiles.filter((f) => f.size <= maxFileSize);
    if (validFiles.length !== acceptedFiles.length) {
      setErrorMsg("Sommige bestanden waren te groot (max 5MB).");
    }

    const compressedFiles = await Promise.all(
      validFiles.map((file) =>
        imageCompression(file, {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
          fileType: "image/webp",
          initialQuality: 0.9,
        })
      )
    );

    const mapped = compressedFiles.map((file) => ({
      isExisting: false,
      file,
      preview: URL.createObjectURL(file),
    }));

    setImages((prev) => [...prev, ...mapped]);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: { "image/jpeg": [], "image/png": [], "image/webp": [] },
    multiple: true,
    onDrop,
  });

  // ─── Image management ─────────────────────────────────────────────────────
  const handleDeleteImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
    if (clickCountIndex === index) {
      setClickCountIndex(null);
      setClickCount(0);
    }
  };

  const handleThumbnailClick = (index) => {
    if (index === clickCountIndex) {
      const newCount = clickCount + 1;
      setClickCount(newCount);

      if (newCount === 3) {
        setImages((prev) => {
          const newArr = [...prev];
          const [movedItem] = newArr.splice(index, 1);
          newArr.unshift(movedItem);
          return newArr;
        });
        setClickCountIndex(null);
        setClickCount(0);
        setSelectedIndex(0);
      } else {
        setSelectedIndex(index);
      }
    } else {
      setClickCountIndex(index);
      setClickCount(1);
      setSelectedIndex(index);
    }
  };

  const handleDragStart = (index) => setDraggedIndex(index);
  const handleDragEnter = (index) => setDragOverIndex(index);
  const handleDragOver = (event) => event.preventDefault();
  const handleDragEnd = () => {
    if (
      draggedIndex !== null &&
      dragOverIndex !== null &&
      draggedIndex !== dragOverIndex
    ) {
      setImages((prev) => {
        const newArr = [...prev];
        const item = newArr[draggedIndex];
        newArr.splice(draggedIndex, 1);
        newArr.splice(dragOverIndex, 0, item);
        return newArr;
      });
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  // ─── Submit ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!isAdmin) {
      setErrorMsg("Je hebt geen beheerdersrechten om dit te doen.");
      return;
    }

    if (!isEditMode && images.length === 0) {
      setErrorMsg("Je moet minimaal één afbeelding uploaden.");
      return;
    }

    setUploading(true);
    setErrorMsg("");
    setUploadProgress(0);

    // Delete removed images from Storage
    const currentExistingUrls = images
      .filter((img) => img.isExisting)
      .map((img) => img.url);

    const removedImages = originalUrls.filter(
      (oldUrl) => !currentExistingUrls.includes(oldUrl)
    );

    for (const removed of removedImages) {
      try {
        await deleteObject(ref(storage, removed));
      } catch {
        // Ignore — file may already be gone
      }
    }

    // Upload new images
    const newImageItems = images.filter((img) => !img.isExisting);
    const newUrls = [];

    for (const item of newImageItems) {
      const uniqueFileName = `${uuidv4()}-${item.file.name}`;
      const storageRef = ref(storage, `fotos/${user.uid}/${uniqueFileName}`);
      const uploadTask = uploadBytesResumable(storageRef, item.file);

      try {
        await new Promise((resolve, reject) => {
          uploadTask.on(
            "state_changed",
            (snapshot) => {
              const progress =
                (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
              setUploadProgress(progress.toFixed(0));
            },
            (err) => {
              // Surface the actual Firebase Storage error
              if (err.code === "storage/unauthorized") {
                reject(
                  new Error(
                    "Geen toestemming voor Firebase Storage. " +
                    "Controleer of jouw Firestore gebruikersdocument isAdmin: true heeft " +
                    "en of de Storage-regels correct zijn."
                  )
                );
              } else {
                reject(err);
              }
            },
            async () => {
              const fileUrl = await getDownloadURL(uploadTask.snapshot.ref);
              newUrls.push(fileUrl);
              resolve();
            }
          );
        });
      } catch (err) {
        setErrorMsg(err.message || "Upload mislukt. Probeer opnieuw.");
        setUploading(false);
        return;
      }
    }

    // Build final URL list preserving order
    const finalUrls = [];
    let newUrlIndex = 0;

    for (const img of images) {
      if (img.isExisting) {
        finalUrls.push(img.url);
      } else {
        finalUrls.push(newUrls[newUrlIndex]);
        newUrlIndex++;
      }
    }

    // Save to Firestore
    try {
      if (isEditMode) {
        await updateDoc(doc(db, "fotos", editId), {
          title: title || "",
          subDescription: subDescription || "",
          description: description || "",
          images: finalUrls,
          updatedAt: serverTimestamp(),
        });
        alert("Foto('s) succesvol bijgewerkt!");
      } else {
        await addDoc(collection(db, "fotos"), {
          userId: user.uid,
          title: title || "",
          subDescription: subDescription || "",
          description: description || "",
          images: finalUrls,
          createdAt: serverTimestamp(),
        });
        alert("Upload geslaagd!");
      }
    } catch (err) {
      setErrorMsg("Opslaan in Firestore mislukt. Probeer opnieuw.");
      setUploading(false);
      return;
    }

    setUploading(false);
    navigate("/fotos");
  };

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) return <p>Authenticatie laden...</p>;
  if (error) return <p>Fout: {error.message}</p>;

  const mainImage = images[selectedIndex];
  let mainImageUrl = "";
  if (mainImage) {
    mainImageUrl = mainImage.isExisting ? mainImage.url : mainImage.preview;
  }

  return (
    <section className="upload-section">
      <h2>{isEditMode ? "Bewerk Foto's" : "Upload Foto's"}</h2>

      {/* Admin warning banner */}
      {!isAdmin && !loading && (
        <div className="error-message" style={{ marginBottom: "1rem" }}>
          ⚠️ Je bent ingelogd, maar hebt geen beheerdersrechten.
          Zorg dat jouw Firestore gebruikersdocument (<code>users/{user?.uid}</code>)
          het veld <strong>isAdmin: true</strong> bevat.
        </div>
      )}

      {mainImage && (
        <div className="main-image-container">
          <img src={mainImageUrl} alt="" className="main-image-preview" />
          <button
            className="delete-button-floating"
            onClick={() => handleDeleteImage(selectedIndex)}
          >
            X
          </button>
        </div>
      )}

      <div className="thumbnail-grid">
        {images.map((img, i) => {
          const thumbUrl = img.isExisting ? img.url : img.preview;
          return (
            <div
              key={thumbUrl + i}
              className="thumbnail-item"
              draggable
              onDragStart={() => handleDragStart(i)}
              onDragEnter={() => handleDragEnter(i)}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              onClick={() => handleThumbnailClick(i)}
            >
              <img src={thumbUrl} alt="" />
              <button
                className="delete-button-floating"
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteImage(i);
                }}
              >
                X
              </button>
            </div>
          );
        })}
      </div>

      <div
        {...getRootProps()}
        className={`drag-drop-area ${isDragActive ? "drag-over" : ""}`}
      >
        <input {...getInputProps()} />
        <p>
          Sleep en zet hier afbeeldingen neer, of klik om te bladeren (max 5MB)
        </p>
      </div>

      <p>Titel</p>
      <input
        type="text"
        placeholder="Titel (optioneel)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <p>Korte beschrijving</p>
      <input
        type="text"
        placeholder="Korte beschrijving (optioneel)"
        value={subDescription}
        onChange={(e) => setSubDescription(e.target.value)}
      />

      <p>Uitgebreidere beschrijving</p>
      <textarea
        placeholder="Uitgebreidere beschrijving (optioneel)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />

      {errorMsg && <p className="error-message">{errorMsg}</p>}
      {uploading && <p>Uploading: {uploadProgress}%</p>}

      <button
        className="Upload"
        onClick={handleSubmit}
        disabled={uploading || !isAdmin}
      >
        {uploading ? "Verwerken..." : isEditMode ? "Bijwerken" : "Upload"}
      </button>
    </section>
  );
};

export default UploadImg;